/* eslint-disable react/prop-types */
/* eslint-disable @next/next/no-sync-scripts */
import { ApolloConsumer, ApolloProvider } from '@apollo/client';
import { defineCustomElements } from '@rccl/global-login/loader';
import '@rcg/cruise-pulse/style.css';
import { FavoritesProvider } from '@rcg/favorites';
import '@rcg/favorites/style.css';
import { COHORTS_COOKIE_NAME, COHORTS_COOKIE_PATH, COHORTS_COOKIE_SAMESITE, updateCohort } from '@rcg/primitive-utils/cohorts-cookie';
import { UserProvider as UserContextProvider } from '@rcg/ui-user';
import { AppWrapper } from 'components/AppWrapper';
import HTMLContainer from 'components/HTMLContainer';
import { MainContainer } from 'components/MainContainer';
import DefaultScroller from 'components/Scroller';
import ScrollerContextProvider from 'components/Scroller/context';
import SearchHeader from 'components/SearchHeader';
import AnalyticsContextProvider from 'contexts/AnalyticsContext';
import { ConfigProvider } from 'contexts/Config';
import { ADULTS, BRANDS, CHILDREN, SEARCH, START_DATE, searchDefault } from 'contexts/CruiseSearchContext/constants';
import makeCalendars from 'contexts/CruiseSearchContext/makeCalendars';
import universalNavigationDefaultContent from 'contexts/CruiseSearchContext/universalNavigation.json';
import { makeStoreFromRouteQuery, urlParamsToObject, urlQueryParser } from 'contexts/CruiseSearchContext/utils/filterParser';
import { getMetadataValues } from 'contexts/CruiseSearchContext/utils/metadataUtils';
import { validateRulesOccupancy } from 'contexts/CruiseSearchContext/utils/occupancy';
import CruiseSelectionContextProvider from 'contexts/CruiseSelectionContext';
import PersonalizationContextProvider from 'contexts/PersonalizationContext/PersonalizationContext';
import TargetedOffersContextProvider from 'contexts/TargetedOffersContext';
import { ThemeProvider } from 'contexts/Theme';
import randomUUID from 'crypto-randomuuid';
import Cookies from 'js-cookie';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { getMarketingScripts, getUniversalNavigation } from 'lib/api/queries/globalization';
import { UNIVERSAL_NAVIGATION_ERROR } from 'lib/api/queries/globalization/constants';
import { getBrandCodeByHost, getClientSideConfig, getServerSideConfig } from 'lib/config';
import { CRUISE_SEARCH_APP_ID, SERVER_TOGGLES } from 'lib/constants';
import { createScript, loadDictionaries, loadLocale } from 'lib/globalization';
import { cookieValidator, getCookie, isNonProd } from 'lib/globalization/Util';
import { createApolloClient, useApollo } from 'lib/graphql/apolloClient';
import { getFilterOptionsFromSource } from 'lib/graphql/cruises/cruises.client';
import { keyBy } from 'lib/helpers';
import { getLDProviderConfig } from 'lib/launchDarkly/csr';
import App from 'next/app';
import Head from 'next/head';
import { memo, useEffect, useState } from 'react';
import GlobalStyles from 'styles/GlobalStyles';
import 'styles/fonts.css';
import { hideAll } from 'tippy.js';
import { url as urlRegExp } from '../components/NLSearch/regexp-patterns';
import { OrderedScripts } from '../components/OrderedScripts/orderedScripts';
import { updateUrlParams } from '../contexts/CruiseSearchContext/utils/urlParamsUtils';
import getNavigationFile from '../lib/api/queries/universalNavigation';
import { MARKETING_SCRIPT_ID } from '../lib/globalization/Scripts/constants';
import { getServerToggles } from '../lib/launchDarkly/ssr';
import { logError, logInfo, logWarn } from '../lib/utilities/logger';
import { themes } from '../styles/theme.css.ts';

const orderedScriptsKeys = [
	MARKETING_SCRIPT_ID.oneTrustBanner,
	MARKETING_SCRIPT_ID.autoBlock,
	MARKETING_SCRIPT_ID.cookieLaw,
	MARKETING_SCRIPT_ID.tealiumInline,
];

const setInitialCookies = (envColor) => {
	Cookies.set('cruiseSearchColor', envColor);

	const cohortsCookie = updateCohort(Cookies.get(COHORTS_COOKIE_NAME), CRUISE_SEARCH_APP_ID, envColor);

	Cookies.set(COHORTS_COOKIE_NAME, cohortsCookie, {
		path: COHORTS_COOKIE_PATH,
		samesite: COHORTS_COOKIE_SAMESITE,
	});
};

const CustomApp = memo(({ Component, pageProps, initialCruiseSearchState = {} }) => {
	const {
		brand: brandCode,
		locale: initLocale,
		metadata: serverSideMetadata,
		queryStringParsed,
		ssrFlags,
		universalNavigationHTML,
		filterOptionsDictionary,
		dictionaries,
		//
		env,
		envColor,
		envGraphColor,
		bypassApolloGateway,
		appVersion,
		tealiumPosition,
		oneTrustScriptStrategy,
	} = initialCruiseSearchState;

	setInitialCookies(envColor);

	const { query = {} } = urlParamsToObject(queryStringParsed);
	const urlParamsObject = makeStoreFromRouteQuery(query);

	const { search } = urlParamsObject;
	const filters = {
		adults: Number(search.adults || 2),
		children: Number(search.children || 0),
	};

	const config = getClientSideConfig(brandCode, env, envColor, appVersion);
	const {
		baseUrl,
		brand,
		publicKeys,
		resources: { favicon },
	} = config;

	const [locale] = useState(initLocale);
	const [marketingScripts] = useState(getMarketingScripts(locale?.market, config, { tealiumPosition, oneTrustScriptStrategy }));
	const orderedScripts = marketingScripts.filter((script) => orderedScriptsKeys.includes(script.id));
	const commonScripts = marketingScripts.filter((script) => !orderedScriptsKeys.includes(script.id));

	const apolloClient = useApollo({
		config,
		envGraphColor,
		bypassApolloGateway,
		locale,
	});

	const launchDarklyConfig = getLDProviderConfig(publicKeys.launchDarklyClientSideId, brand?.code, envColor, locale);

	const fullBrandSettings = BRANDS.find((b) => b.code === brand.code);

	useEffect(() => {
		if (locale) {
			window.cookieValidator = cookieValidator;
			window.getCookie = getCookie;
			window.isNonProd = isNonProd;
			Cookies.set('currency', locale.currency, {
				domain: baseUrl.replace('https://www', ''),
			});
		}
	}, [baseUrl, locale?.currency]);

	useEffect(() => {
		window.addEventListener('scroll', () => {
			const tooltipOpen = document.querySelector('[data-tippy-root]');
			const tooltipNlSearch = document.getElementById('nl-search-tooltip');

			if (tooltipOpen && !tooltipNlSearch) {
				hideAll();
			}
			return () => {
				window.removeEventListener('scroll');
			};
		});
	}, []);

	useEffect(() => {
		if (window) {
			void defineCustomElements(window);
		}
	}, []);

	if (!brandCode) {
		return null;
	}

	return (
		<>
			<Head>
				<link rel="shortcut icon" href={favicon} />
				<style>{'#optanon-popup-bg{z-index:9999 !important}'}</style>
			</Head>
			<SearchHeader
				serverSideMetadata={serverSideMetadata}
				filterOptionsDictionary={filterOptionsDictionary}
				config={config}
				locale={locale}
				enablePageIndexing={ssrFlags[SERVER_TOGGLES.ENABLE_PAGE_INDEXING]}
				dictionaries={dictionaries}
			/>
			<div
				id="marketRootPath"
				data-marketrootpath={`/content/royal/${locale.market}`}
				data-globalizationenable="true"
				data-languagepage={locale.language}
			></div>
			<OrderedScripts scripts={orderedScripts} />
			{commonScripts.map((script) => createScript(script, ssrFlags))}
			<GlobalStyles />
			{!universalNavigationHTML?.header?.includes(UNIVERSAL_NAVIGATION_ERROR) && (
				<HTMLContainer id="aemHeaderContainer" html={universalNavigationHTML?.header} />
			)}
			<div id="root" className={themes[brandCode]}>
				<MainContainer>
					<ConfigProvider config={config}>
						<LDProvider {...launchDarklyConfig}>
							<ApolloProvider client={apolloClient}>
								<ApolloConsumer>
									{(client) => (
										<AppWrapper apolloClient={client} config={config} initialState={initialCruiseSearchState}>
											<CruiseSelectionContextProvider>
												<AnalyticsContextProvider brand={brand.code}>
													<PersonalizationContextProvider>
														<FavoritesProvider
															config={{
																brand: brandCode,
																locale: locale,
															}}
															env={config.guestAccountEnv}
														>
															<UserContextProvider brand={fullBrandSettings.nameOneWord} filters={filters}>
																<TargetedOffersContextProvider apolloClient={client} initialState={initialCruiseSearchState}>
																	<ScrollerContextProvider>
																		<DefaultScroller>
																			<ThemeProvider brandCode={brand.code}>
																				<Component brand={brand.code} {...pageProps} locale={locale} />
																			</ThemeProvider>
																		</DefaultScroller>
																	</ScrollerContextProvider>
																</TargetedOffersContextProvider>
															</UserContextProvider>
														</FavoritesProvider>
													</PersonalizationContextProvider>
												</AnalyticsContextProvider>
											</CruiseSelectionContextProvider>
										</AppWrapper>
									)}
								</ApolloConsumer>
							</ApolloProvider>
						</LDProvider>
					</ConfigProvider>
				</MainContainer>
			</div>
			<input id="app-version" type="hidden" value={appVersion} />
			{!universalNavigationHTML?.footer?.includes(UNIVERSAL_NAVIGATION_ERROR) && (
				<HTMLContainer id="aemFooterContainer" html={universalNavigationHTML?.footer} />
			)}
		</>
	);
});

CustomApp.getInitialProps = async (appContext) => {
	const logId = randomUUID();

	try {
		const { query, asPath, req, res } = appContext?.ctx || {};
		const { cookies, headers, url } = req || {};
		const { host = '' } = headers || {};
		const activeColor = process.env.ENVIRONMENT_COLOR;
		const urlParams = asPath || '';

		if (activeColor === null) {
			logWarn("Couldn't determine active color", {
				logId,
			});
		}

		logInfo('Request received', {
			hostFromHeader: host,
			urlParams,
			activeColor,
			headers,
			url,
			logId,
		});

		const appProps = await App.getInitialProps(appContext);
		const brandCode = getBrandCodeByHost(host);

		if (!brandCode) {
			logError("Couldn't determine brand", { hostFromHeader: host, logId });
			res.statusCode = 400;

			return { ...appProps };
		}

		const serverSideConfig = getServerSideConfig(brandCode);
		if (!serverSideConfig) {
			logError("getInitialProps error: Couldn't determine serverSideConfig", { brandCode, serverSideConfig, logId });
		}

		const locale = await loadLocale(host, query, cookies, serverSideConfig, logId, activeColor);
		if (!locale || !locale.language) {
			logError("getInitialProps error: Couldn't determine locale", { host, query, cookies, serverSideConfig, activeColor, logId });
		}

		const serverToggles = await getServerToggles(locale, serverSideConfig, logId, 'cruiseSearch', cookies.rwd_id);
		const ssrCommonFlags = await getServerToggles(locale, serverSideConfig, logId, 'common', cookies.rwd_id);

		let [, params = ''] = urlQueryParser(asPath, brandCode)?.split('?') || [];

		const { params: updatedParams, needsToRedirect } = updateUrlParams(url, query, params, locale);
		params = updatedParams;

		let asPathParamsAll = `?${params}`;

		if (needsToRedirect) {
			const redirectUrl = `${locale.path ?? '/'}cruises${asPathParamsAll}`;

			logInfo('Server redirect', {
				from: url,
				to: redirectUrl,
				activeColor,
				logId,
			});

			res.writeHead(301, { Location: redirectUrl });
			res.end();
			return {};
		}

		// Handle locale path redirect on server side
		if (req && res) {
			const page = asPath?.split('/')?.pop()?.split('?')[0] || 'cruises';
			const { market = '', language = '' } = query;
			const oldPath = market ? `/${[market, language]?.filter(Boolean).join('/')}/` : '/';

			// Extract query parameters
			const queryPath = params ? `?${params}` : '';

			if (oldPath !== locale.path) {
				const isExternalRedirect = urlRegExp.test(locale.path);
				const redirectUrl = isExternalRedirect ? locale.path : `${locale.path}${page}${queryPath}`;

				logInfo('Server redirect', {
					from: url,
					to: redirectUrl,
					activeColor,
					logId,
				});

				res.writeHead(301, { Location: redirectUrl });
				res.end();
				return {};
			}
		}

		// Extract query parameters to check for graph-app-version
		const queryParams = new URLSearchParams(params);

		// Get envGraphColor from server toggles with fallback to query parameter
		const bypassApolloGateway = serverToggles[SERVER_TOGGLES.BYPASS_APOLLO_GATEWAY_FOR_CS_CALLS];
		let envGraphColor = serverToggles[SERVER_TOGGLES.GRAPH_BLUE_GREEN_COLOR];

		// If not set from server toggles, check if it's in the URL parameters
		if (!envGraphColor && queryParams.has('graph-app-version')) {
			const graphAppVersion = queryParams.get('graph-app-version');
			// Ensure the value is a valid EnvironmentColor (blue or green)
			if (graphAppVersion && ['blue', 'green'].includes(graphAppVersion.toLowerCase())) {
				envGraphColor = graphAppVersion.toLowerCase();
			}
		}

		const apolloClient = createApolloClient({
			host,
			locale,
			envGraphColor,
			bypassApolloGateway,
			config: serverSideConfig,
			logId,
		});
		const filterOptions = await getFilterOptionsFromSource(apolloClient, {
			brand: serverSideConfig.brand.code,
			country: locale.country,
			language: locale.language,
			currency: locale.currency,
			office: locale.office,
			logId,
		});

		if (!filterOptions) {
			logError("getInitialProps error: Couldn't determine filterOptions", {
				brand: serverSideConfig.brand.code,
				country: locale.country,
				language: locale.language,
				currency: locale.currency,
				office: locale.office,
				logId,
			});
		}

		const filterOptionsDictionary = keyBy(filterOptions, 'key');

		if (!filterOptionsDictionary[START_DATE]) {
			logError("getInitialProps error: Couldn't determine filterOptionsDictionary[START_DATE]", {
				filterOptionsDictionary,
			});
		}
		const startDateFilter = filterOptionsDictionary[START_DATE] || {};
		const { dateRangeCalendar, daysOfWeek, monthsCalendar } = makeCalendars(startDateFilter, locale);

		let queryStringParsed = urlQueryParser(asPathParamsAll, brandCode);
		const urlParamsObject = makeStoreFromRouteQuery(urlParamsToObject(queryStringParsed));
		const { search } = urlParamsObject;

		if ([ADULTS, CHILDREN].some((item) => Object.keys(search || {}).includes(item))) {
			const restParams = queryStringParsed.split('&')?.filter((item) => item.split('=')?.[0] !== SEARCH);
			restParams.push(validateRulesOccupancy(search));
			queryStringParsed = restParams.filter((item) => Boolean(item)).join('&');
		}

		if (!urlParamsObject || !asPath || !serverSideConfig) {
			logError("getInitialProps error: Couldn't determine metadata", {
				urlParamsObject,
				asPath,
				serverSideConfig,
			});
		}

		const alerts =
			serverToggles && Array.isArray(serverToggles[SERVER_TOGGLES.SHOW_ALERTS_ON_HEADER]) ? serverToggles[SERVER_TOGGLES.SHOW_ALERTS_ON_HEADER] : [];
		const promotions = { valid: false };
		// universal navigation
		//  ui built as part of the app. content served from json file hosted on the aem dam
		let universalNavigationContent = null;
		//  ui and content hosted on an aem experience fragment
		let universalNavigationHTML = null;

		universalNavigationHTML = await getUniversalNavigation(locale, serverSideConfig, logId, host);
		if (
			universalNavigationHTML?.header?.includes(UNIVERSAL_NAVIGATION_ERROR) ||
			universalNavigationHTML?.footer?.includes(UNIVERSAL_NAVIGATION_ERROR)
		) {
			const universalNavigationExternalContent = await getNavigationFile(locale, serverSideConfig, logId);
			universalNavigationContent = universalNavigationExternalContent || universalNavigationDefaultContent;
		}

		const dictionaries = await loadDictionaries(locale.language, serverSideConfig, logId);
		const metadata = getMetadataValues(
			urlParamsObject,
			'',
			filterOptionsDictionary,
			asPath,
			serverSideConfig,
			locale,
			dictionaries.cruiseSearchDictionary,
		);

		const tealiumPosition = serverToggles[SERVER_TOGGLES.MOVE_TEALIUM_SCRIPT_INSIDE_HEAD] ? 'beforeInteractive' : 'afterInteractive';

		const oneTrustScriptStrategy = serverToggles[SERVER_TOGGLES.MOVE_ONETRUST_SCRIPT_INSIDE_HEAD] ? 'beforeInteractive' : 'afterInteractive';

		const initialCruiseSearchState = {
			alerts,
			searchApplied: searchDefault,
			searchSelecting: searchDefault,
			loading: false,
			metadata,
			filterOptions,
			filterOptionsDictionary,
			dateRangeCalendar,
			monthsCalendar,
			daysOfWeek,
			queryStringParsed,
			ssrFlags: { ...serverToggles },
			brand: brandCode,
			ssrCommonFlags,
			locale,
			dictionaries,
			universalNavigationContent,
			universalNavigationHTML,
			promotions,
			//
			env: serverSideConfig.env,
			envColor: serverSideConfig.envColor,
			appVersion: serverSideConfig.appVersion,
			envGraphColor,
			bypassApolloGateway,
			logId,
			tealiumPosition,
			oneTrustScriptStrategy,
		};

		return { ...appProps, initialCruiseSearchState };
	} catch (err) {
		logError('Error in getInitialProps', { err, logId });
	}
};

export default CustomApp;
